// Typed fetch wrapper for the InCorpoSano API.
//
// Types are pulled from the openapi-typescript-generated schema.d.ts so any
// backend-breaking change fails `tsc` at the frontend boundary, not at
// runtime. This module is intentionally tiny: one createClient() factory,
// one ApiError, no runtime dependencies.

import type { components } from "./schema";

// Re-export the strict response shapes so consumers don't have to know
// where they came from.
export type HealthResponse = components["schemas"]["HealthResponse"];
export type CreateSessionResponse = components["schemas"]["CreateSessionResponse"];
export type ActiveSessionResponse = components["schemas"]["ActiveSessionResponse"];
export type SessionSummary = components["schemas"]["SessionSummary"];
export type DeleteLastPairResponse = components["schemas"]["DeleteLastPairResponse"];
export type UserProfileSummaryResponse = components["schemas"]["UserProfileSummaryResponse"];
export type ChatTurn = components["schemas"]["ChatTurn"];
export type ChatRequest = components["schemas"]["ChatRequest"];
export type ChatResponse = components["schemas"]["ChatResponse"];
export type RecommendRequest = components["schemas"]["RecommendRequest"];
export type RecommendResponse = components["schemas"]["RecommendResponse"];
export type RecommendAudit = components["schemas"]["RecommendAudit"];
export type IngredientSummary = components["schemas"]["IngredientSummary"];
export type SkuSummary = components["schemas"]["SkuSummary"];
export type TenantBrandingResponse = components["schemas"]["TenantBrandingResponse"];

// ─── Auth payloads (hand-written; mirror backend/src/incorposano/api/auth_schemas.py) ──
export interface SignupRequest {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirm: string;
  gdpr_accepted: boolean;
  // ICSP-43: explicit adult self-certification, required to register.
  adult_declared: boolean;
}

export interface SignupResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  email_verification_required: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface MeResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  email_verified: boolean;
  gdpr_version: string;
  gdpr_accepted_at: string;
  // ICSP-43: True once the user has self-certified as an adult.
  adult_declared: boolean;
  created_at: string;
}

export interface AuthTokenResponse {
  access_token: string;
  token_type: string;
  user: MeResponse;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirm {
  token: string;
  password: string;
  password_confirm: string;
}

export interface GenericOkResponse {
  ok: boolean;
}

export interface ClientOptions {
  /** Base URL of the API, e.g. "https://api.incorposano.it" or
   *  "http://localhost:8000" — no trailing slash required. */
  baseUrl: string;
  /** Inject a custom fetch (for tests, MSW, AbortController plumbing). */
  fetchImpl?: typeof fetch;
  /** Extra headers merged into every request (e.g. Authorization later). */
  headers?: Record<string, string>;
  /** Resolved fresh on every request — used to inject the JWT bearer. Returning
   *  null/undefined skips the Authorization header. */
  getAuthToken?: () => string | null | undefined;
}

/**
 * Thrown when the backend returns a non-2xx status. Body is preserved verbatim
 * (truncated in the exception message to avoid log spam) so callers can inspect.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
    public readonly url: string,
  ) {
    super(`InCorpoSano API ${status} on ${url}: ${body.slice(0, 200)}`);
    this.name = "ApiError";
  }
}

export interface IncorpoSanoClient {
  health(): Promise<HealthResponse>;
  createSession(): Promise<CreateSessionResponse>;
  getActiveSession(): Promise<ActiveSessionResponse | null>;
  // ICSP-37: fallback for /recommendation mount when the active-session
  // query returns null (status=complete after PROFILO PRONTO). Returns
  // the user's most recent COMPLETED session or null.
  getLatestCompletedSession(): Promise<ActiveSessionResponse | null>;
  getSession(sessionId: string): Promise<ActiveSessionResponse>;
  listSessions(): Promise<SessionSummary[]>;
  deleteSession(sessionId: string): Promise<void>;
  deleteLastMessagePair(sessionId: string): Promise<DeleteLastPairResponse>;
  getMyProfileSummary(): Promise<UserProfileSummaryResponse>;
  sendMessage(sessionId: string, body: ChatRequest): Promise<ChatResponse>;
  recommend(sessionId: string, body?: RecommendRequest): Promise<RecommendResponse>;
  getRecommendation(sessionId: string): Promise<RecommendResponse>;
  listIngredients(): Promise<IngredientSummary[]>;
  listSkus(): Promise<SkuSummary[]>;
  tenantBranding(): Promise<TenantBrandingResponse>;

  // ─── auth ──
  signup(body: SignupRequest): Promise<SignupResponse>;
  verifyEmail(token: string): Promise<GenericOkResponse>;
  login(body: LoginRequest): Promise<AuthTokenResponse>;
  me(): Promise<MeResponse>;
  // ICSP-43: record an adult self-certification for the current user
  // (pre-chat gate, legacy accounts). Idempotent; returns the updated /me.
  declareAdult(): Promise<MeResponse>;
  passwordResetRequest(body: PasswordResetRequest): Promise<GenericOkResponse>;
  passwordResetConfirm(body: PasswordResetConfirm): Promise<GenericOkResponse>;
}

export function createClient(opts: ClientOptions): IncorpoSanoClient {
  const fetchImpl = opts.fetchImpl ?? fetch;
  const baseUrl = opts.baseUrl.replace(/\/$/, "");
  const baseHeaders: Record<string, string> = {
    "content-type": "application/json",
    accept: "application/json",
    ...opts.headers,
  };
  const getAuthToken = opts.getAuthToken;

  async function call<T>(path: string, init?: RequestInit): Promise<T> {
    const url = `${baseUrl}${path}`;
    const authHeader: Record<string, string> = {};
    const token = getAuthToken?.();
    if (token) authHeader["authorization"] = `Bearer ${token}`;

    const response = await fetchImpl(url, {
      ...init,
      headers: { ...baseHeaders, ...authHeader, ...init?.headers },
    });
    if (!response.ok) {
      const bodyText = await response.text().catch(() => "");
      throw new ApiError(response.status, bodyText, url);
    }
    if (response.status === 204) return undefined as unknown as T;
    return (await response.json()) as T;
  }

  return {
    health: () => call<HealthResponse>("/api/v1/health"),

    tenantBranding: () =>
      call<TenantBrandingResponse>("/api/v1/tenant/branding"),

    createSession: () =>
      call<CreateSessionResponse>("/api/v1/sessions", { method: "POST", body: "{}" }),

    getActiveSession: () =>
      call<ActiveSessionResponse | null>("/api/v1/sessions/active"),

    getLatestCompletedSession: () =>
      call<ActiveSessionResponse | null>("/api/v1/sessions/latest-completed"),

    getSession: (sessionId) =>
      call<ActiveSessionResponse>(
        `/api/v1/sessions/${encodeURIComponent(sessionId)}`,
      ),

    listSessions: () => call<SessionSummary[]>("/api/v1/sessions"),

    deleteSession: (sessionId) =>
      call<void>(`/api/v1/sessions/${encodeURIComponent(sessionId)}`, {
        method: "DELETE",
      }),

    deleteLastMessagePair: (sessionId) =>
      call<DeleteLastPairResponse>(
        `/api/v1/sessions/${encodeURIComponent(sessionId)}/messages/last`,
        { method: "DELETE" },
      ),

    getMyProfileSummary: () =>
      call<UserProfileSummaryResponse>("/api/v1/me/profile-summary"),

    sendMessage: (sessionId, body) =>
      call<ChatResponse>(
        `/api/v1/sessions/${encodeURIComponent(sessionId)}/messages`,
        { method: "POST", body: JSON.stringify(body) },
      ),

    recommend: (sessionId, body = {}) =>
      call<RecommendResponse>(
        `/api/v1/sessions/${encodeURIComponent(sessionId)}/recommend`,
        { method: "POST", body: JSON.stringify(body) },
      ),

    getRecommendation: (sessionId) =>
      call<RecommendResponse>(
        `/api/v1/sessions/${encodeURIComponent(sessionId)}/recommendation`,
      ),

    listIngredients: () => call<IngredientSummary[]>("/api/v1/catalog/ingredients"),

    listSkus: () => call<SkuSummary[]>("/api/v1/catalog/skus"),

    // ─── auth ──
    signup: (body) =>
      call<SignupResponse>("/api/v1/auth/signup", {
        method: "POST",
        body: JSON.stringify(body),
      }),

    verifyEmail: (token) =>
      call<GenericOkResponse>(
        `/api/v1/auth/verify-email?token=${encodeURIComponent(token)}`,
        { method: "POST" },
      ),

    login: (body) =>
      call<AuthTokenResponse>("/api/v1/auth/login", {
        method: "POST",
        body: JSON.stringify(body),
      }),

    me: () => call<MeResponse>("/api/v1/auth/me"),

    declareAdult: () =>
      call<MeResponse>("/api/v1/auth/declare-adult", { method: "POST" }),

    passwordResetRequest: (body) =>
      call<GenericOkResponse>("/api/v1/auth/password/reset/request", {
        method: "POST",
        body: JSON.stringify(body),
      }),

    passwordResetConfirm: (body) =>
      call<GenericOkResponse>("/api/v1/auth/password/reset/confirm", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  };
}
